import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "./api";

/**
 * useResource — one GET, with loading, error and a reload().
 *
 * `onAuthFail` is passed in rather than imported so that signing out lives in
 * exactly one place (App). A hook that can log the user out from anywhere is a
 * hook you cannot reason about.
 */
export function useResource(path, { skip = false, onAuthFail } = {}) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(!skip);
  const [tick, setTick] = useState(0);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  useEffect(() => {
    if (skip || !path) { setLoading(false); return; }
    let dead = false;
    setLoading(true);
    setError("");
    api
      .get(path)
      .then((d) => { if (!dead) setData(d); })
      .catch((e) => {
        if (dead) return;
        if (e.status === 401 && onAuthFail) onAuthFail();
        else setError(e.message || "Something went wrong.");
      })
      .finally(() => { if (!dead) setLoading(false); });
    return () => { dead = true; };
  }, [path, skip, tick, onAuthFail]);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { data, error, loading, reload, setData };
}
